import {CacheKeyConfig, useGrpcRequest} from '@aboutyou/data-fetcher';
import {CategoryFiltersService_GetFiltersLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/filters/service.grpc.lazy';
import {useCustomerLikedBrandIds, useOptionalSession} from '@aboutyou/user';
import {useCallback, useRef} from 'react';
import {
  useAppliedLiveAtFilter,
  useAppliedPotentialFilters,
} from '../../../../features/ProductStream/utils';
import {useReductionsStateRequestParameter} from '../../../../features/ReductionsState/useReductionsStateRequestParameter';
import {
  useAppliedQuickFilter,
  useCategoryCacheKeyTransformer,
  useCategoryIdentifier,
} from '../../utils';

export const useCategoryStreamFiltersData = () => {
  const appliedFilters = useAppliedPotentialFilters();
  const appliedQuickFilter = useAppliedQuickFilter();
  const categoryIdentifier = useCategoryIdentifier();
  const session = useOptionalSession();
  const liveAtFilter = useAppliedLiveAtFilter();
  const filterCacheKeyTransformer = useFilterCacheKeyTransformer();

  const reductionsState = useReductionsStateRequestParameter();

  return useGrpcRequest(
    CategoryFiltersService_GetFiltersLazy,
    {
      appliedFilters: {
        filters: appliedFilters,
        quickFilter: appliedQuickFilter,
        liveAt: liveAtFilter
          ? {
              minDate: liveAtFilter,
            }
          : undefined,
      },
      category: {
        identifier: categoryIdentifier,
      },
      reductionsState,
      session,
      sortOptions: null,
    },
    {keepPreviousData: true, transformCacheKey: filterCacheKeyTransformer},
  );
};

const useFilterCacheKeyTransformer = () => {
  const categoryCacheKeyTransformer = useCategoryCacheKeyTransformer();
  // never update favorite brands while the user is on the page to only validate once at enter. Without the `useRef`, if a logged in user with favorite brands will directly enter a category page, two requests are made to the backend (one for the session and another one for the brand update as soon as they are loaded from the backend).
  const favoriteBrandIds = useRef(useCustomerLikedBrandIds());

  return useCallback(
    (config: CacheKeyConfig) => {
      const {params, ...rest} = categoryCacheKeyTransformer(config);
      return {
        ...rest,
        params: {
          ...params,
          // HACK: The favorite brands are used on the filter in the backend and therefore we need to trigger another request as soon as the liked brands change. By invalidating the cache key for the request this is fairly working as expected. This is a bad practice and just a workaround.Potentially we should better add the favorite brands as a request parameter to the backend in the request parameter. In that case this would work automatically.
          favorite: favoriteBrandIds,
        },
      };
    },
    [categoryCacheKeyTransformer, favoriteBrandIds],
  );
};
